import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/use-auth'
import { api } from '../services/api'
import { colors, card, tableCell } from '../components/ui/styles'
import { PageHeader, StatusBadge } from '../components/ui'

interface DashboardData {
  readonly criticalResults: number
  readonly pendingResults: number
  readonly awaitingRelease: number
  readonly pendingRelease: number
  readonly submittedOrders: number
  readonly qcFailures: number
  readonly devicesOverdue: number
  readonly expiringLots: number
  readonly resultsToday: number
  readonly releasedToday: number
  readonly ordersToday: number
  readonly qcRunsToday: number
  readonly devicesActive: number
}

interface AlertItem {
  readonly id: string
  readonly label: string
  readonly detail: string
  readonly age: string
  readonly link: string
  readonly priority: 'P0' | 'P1' | 'P2'
  readonly type: string
}

const EMPTY_DATA: DashboardData = {
  criticalResults: 0,
  pendingResults: 0,
  awaitingRelease: 0,
  pendingRelease: 0,
  submittedOrders: 0,
  qcFailures: 0,
  devicesOverdue: 0,
  expiringLots: 0,
  resultsToday: 0,
  releasedToday: 0,
  ordersToday: 0,
  qcRunsToday: 0,
  devicesActive: 0,
}

function formatAge(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 60) return `${min}m`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return `${hrs}h ${min % 60}m`
  const days = Math.floor(hrs / 24)
  return `${days}d ${hrs % 24}h`
}

interface RankedItem {
  readonly id: string
  readonly label: string
  readonly detail: string
  readonly age: string
  readonly link: string
}

function RankedList({
  title,
  items,
  emptyMessage,
}: {
  readonly title: string
  readonly items: readonly RankedItem[]
  readonly emptyMessage: string
}) {
  const navigate = useNavigate()

  return (
    <div style={{ ...card, padding: '1rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9375rem', color: colors.textHeading, fontWeight: 600 }}>
        {title}
      </h3>
      {items.length === 0 ? (
        <div style={{ color: colors.textMuted, fontSize: '0.875rem', padding: '0.5rem 0' }}>{emptyMessage}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                onClick={() => navigate(item.link)}
                style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.border}` }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.tableRowHover
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = ''
                }}
              >
                <td style={{ ...tableCell, fontWeight: 500 }}>{item.label}</td>
                <td style={{ ...tableCell, color: colors.textMuted }}>{item.detail}</td>
                <td style={{ ...tableCell, textAlign: 'right', color: colors.warning, fontWeight: 600 }}>{item.age}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

const priorityColors = {
  P0: { bg: '#fef2f2', border: colors.danger, text: colors.danger },
  P1: { bg: '#fffbeb', border: colors.warning, text: '#92400e' },
  P2: { bg: '#f0f4ff', border: colors.primary, text: colors.primary },
}

function AlertCard({
  type,
  count,
  priority,
  description,
  onClick,
}: {
  readonly type: string
  readonly count: number
  readonly priority: 'P0' | 'P1' | 'P2'
  readonly description: string
  readonly onClick?: () => void
}) {
  const pc = priorityColors[priority]
  if (count === 0) return null

  return (
    <div
      onClick={onClick}
      style={{
        ...card,
        borderLeft: `4px solid ${pc.border}`,
        background: pc.bg,
        cursor: onClick ? 'pointer' : undefined,
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.875rem 1rem',
      }}
    >
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color: pc.border, minWidth: '40px' }}>{count}</div>
      <div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: pc.text }}>{type}</div>
        <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginTop: '2px' }}>{description}</div>
      </div>
    </div>
  )
}

function AlertQueue({ items }: { readonly items: readonly AlertItem[] }) {
  const navigate = useNavigate()

  if (items.length === 0) {
    return (
      <div style={{ ...card, textAlign: 'center', color: colors.textMuted, padding: '2rem' }}>
        No items need immediate attention.
      </div>
    )
  }

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: colors.tableHeaderBg }}>
            <th
              style={{
                padding: '0.5rem 0.75rem',
                textAlign: 'left',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
              }}
            >
              Priority
            </th>
            <th
              style={{
                padding: '0.5rem 0.75rem',
                textAlign: 'left',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
              }}
            >
              Item
            </th>
            <th
              style={{
                padding: '0.5rem 0.75rem',
                textAlign: 'left',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
              }}
            >
              Type
            </th>
            <th
              style={{
                padding: '0.5rem 0.75rem',
                textAlign: 'left',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
              }}
            >
              Detail
            </th>
            <th
              style={{
                padding: '0.5rem 0.75rem',
                textAlign: 'right',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
              }}
            >
              Waiting
            </th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 15).map((item) => {
            const pc = priorityColors[item.priority]
            return (
              <tr
                key={item.id}
                onClick={() => navigate(item.link)}
                style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.border}` }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.tableRowHover
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = ''
                }}
              >
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <StatusBadge
                    status={item.priority === 'P0' ? 'critical' : item.priority === 'P1' ? 'warning' : 'active'}
                    label={item.priority}
                  />
                </td>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, fontSize: '0.9375rem' }}>{item.label}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', color: colors.textMuted }}>
                  {item.type}
                </td>
                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', color: colors.text }}>{item.detail}</td>
                <td
                  style={{
                    padding: '0.5rem 0.75rem',
                    textAlign: 'right',
                    fontWeight: 600,
                    color: pc.text,
                    fontSize: '0.875rem',
                  }}
                >
                  {item.age}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardData>(EMPTY_DATA)
  const [alertItems, setAlertItems] = useState<readonly AlertItem[]>([])
  const [pendingList, setPendingList] = useState<readonly RankedItem[]>([])
  const [awaitingList, setAwaitingList] = useState<readonly RankedItem[]>([])
  const [pendingReleaseList, setPendingReleaseList] = useState<readonly RankedItem[]>([])
  const [ordersList, setOrdersList] = useState<readonly RankedItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    type ResultRow = {
      id: string
      barcode: string | null
      testType: string
      status: string
      createdAt: string
      isCritical: boolean
      criticalFlaggedAt: string | null
    }
    type OrderRow = {
      id: string
      orderNumber: string | null
      status: string
      testTypes: readonly string[]
      createdAt: string
      priority: string
    }
    type DashboardSummary = {
      counts: {
        criticalResults: number
        pendingResults: number
        awaitingRelease: number
        pendingRelease: number
        submittedOrders: number
        devicesOverdue: number
        devicesActive: number
        resultsToday: number
        releasedToday: number
        ordersToday: number
      }
      topPending: readonly ResultRow[]
      topAwaiting: readonly ResultRow[]
      topCritical: readonly ResultRow[]
      topPendingRelease: readonly ResultRow[]
      topOrders: readonly OrderRow[]
    }
    type QcSummary = { runsToday: number; failuresToday: number; expiringLots: number }

    Promise.all([
      api.get<DashboardSummary>('/dashboard/summary'),
      api.get<QcSummary>('/qc/summary').catch(() => ({ data: null })),
    ])
      .then(([summaryRes, qcSummaryRes]) => {
        if (cancelled) return
        const summary = summaryRes.data
        if (!summary) {
          setLoading(false)
          return
        }

        const { counts, topPending, topAwaiting, topCritical, topPendingRelease, topOrders } = summary
        const qcSummary = (qcSummaryRes.data as QcSummary | null) ?? {
          runsToday: 0,
          failuresToday: 0,
          expiringLots: 0,
        }

        setData({
          ...counts,
          qcFailures: qcSummary.failuresToday,
          expiringLots: qcSummary.expiringLots,
          qcRunsToday: qcSummary.runsToday,
        })

        const items: AlertItem[] = []

        for (const r of topCritical) {
          items.push({
            id: `crit-${r.id}`,
            label: r.barcode || r.id.slice(0, 8),
            detail: `${r.testType} (CRITICAL)`,
            age: formatAge(r.criticalFlaggedAt ?? r.createdAt),
            link: `/testing`,
            priority: 'P0',
            type: 'Critical result pending',
          })
        }

        for (const r of topPending) {
          items.push({
            id: `result-${r.id}`,
            label: r.barcode || r.id.slice(0, 8),
            detail: r.testType,
            age: formatAge(r.createdAt),
            link: `/testing`,
            priority: 'P1',
            type: 'Stale result',
          })
        }

        for (const r of topAwaiting) {
          items.push({
            id: `awaiting-${r.id}`,
            label: r.barcode || r.id.slice(0, 8),
            detail: r.testType,
            age: formatAge(r.createdAt),
            link: `/testing?tab=clear`,
            priority: 'P1',
            type: 'Awaiting release',
          })
        }

        for (const o of topOrders.filter((o) => o.status === 'submitted')) {
          items.push({
            id: `order-${o.id}`,
            label: o.orderNumber || o.id.slice(0, 8),
            detail: o.testTypes.join(', '),
            age: formatAge(o.createdAt),
            link: `/orders/${o.id}`,
            priority: o.priority === 'stat' ? 'P0' : 'P1',
            type: 'Awaiting accessioning',
          })
        }

        items.sort((a, b) => {
          const po = { P0: 0, P1: 1, P2: 2 }
          return po[a.priority] - po[b.priority]
        })

        setAlertItems(items)

        setPendingList(
          topPending.map((r) => ({
            id: r.id,
            label: r.barcode || r.id.slice(0, 8),
            detail: r.testType,
            age: formatAge(r.createdAt),
            link: `/testing`,
          })),
        )
        setAwaitingList(
          topAwaiting.map((r) => ({
            id: r.id,
            label: r.barcode || r.id.slice(0, 8),
            detail: r.testType,
            age: formatAge(r.createdAt),
            link: `/testing?tab=clear`,
          })),
        )
        setPendingReleaseList(
          topPendingRelease.map((r) => ({
            id: r.id,
            label: r.barcode || r.id.slice(0, 8),
            detail: r.testType,
            age: formatAge(r.createdAt),
            link: `/testing?tab=release`,
          })),
        )
        setOrdersList(
          topOrders
            .filter((o) => o.status !== 'completed' && o.status !== 'canceled')
            .map((o) => ({
              id: o.id,
              label: o.orderNumber || o.id.slice(0, 8),
              detail: o.status.replace(/_/g, ' '),
              age: formatAge(o.createdAt),
              link: `/orders/${o.id}`,
            })),
        )
        setLoading(false)
      })
      .catch(() => setLoading(false))

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div data-testid="dashboard">
      <PageHeader title="Dashboard" subtitle={`Welcome, ${user?.firstName || user?.email}`} />

      {loading ? (
        <div style={{ color: colors.textMuted, padding: '2rem' }}>Loading dashboard...</div>
      ) : (
        <>
          {/* Alert summary cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <AlertCard
              type="Critical Results"
              count={data.criticalResults}
              priority="P0"
              description="Results requiring immediate release"
              onClick={() => navigate('/testing')}
            />
            <AlertCard
              type="QC Failures"
              count={data.qcFailures}
              priority="P0"
              description="Out-of-range QC results"
              onClick={() => navigate('/qc/lots')}
            />
            <AlertCard
              type="Pending Results"
              count={data.pendingResults}
              priority="P1"
              description="Results awaiting entry or progress"
              onClick={() => navigate('/testing')}
            />
            <AlertCard
              type="Awaiting Release"
              count={data.awaitingRelease}
              priority="P1"
              description="Verified results ready for release"
              onClick={() => navigate('/testing?tab=clear')}
            />
            <AlertCard
              type="Pending Release"
              count={data.pendingRelease}
              priority="P1"
              description="Awaiting second-reviewer release"
              onClick={() => navigate('/testing?tab=release')}
            />
            <AlertCard
              type="Orders to Accession"
              count={data.submittedOrders}
              priority="P1"
              description="Submitted orders needing barcodes"
              onClick={() => navigate('/accessioning')}
            />
            <AlertCard
              type="Devices Overdue"
              count={data.devicesOverdue}
              priority="P2"
              description="Instruments past calibration date"
              onClick={() => navigate('/devices')}
            />
          </div>

          {/* Priority queue */}
          {alertItems.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 600, color: colors.textHeading, margin: '0 0 0.5rem' }}>
                Action Queue
              </h2>
              <AlertQueue items={alertItems} />
            </div>
          )}

          {/* Detail lists */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <RankedList title="Longest Pending Results" items={pendingList} emptyMessage="No pending results." />
            <RankedList title="Oldest Open Orders" items={ordersList} emptyMessage="No open orders." />
            <RankedList title="Awaiting Release" items={awaitingList} emptyMessage="No results awaiting release." />
            <RankedList title="Pending Release" items={pendingReleaseList} emptyMessage="No results pending release." />
          </div>

          {/* Summary strip */}
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}
          >
            {[
              { label: 'Results entered today', value: data.resultsToday },
              { label: 'Results released today', value: data.releasedToday },
              { label: 'Orders today', value: data.ordersToday },
              { label: 'QC runs today', value: data.qcRunsToday },
              { label: 'Instruments active', value: data.devicesActive },
            ].map((m) => (
              <div
                key={m.label}
                style={{
                  ...card,
                  padding: '0.6rem 1rem',
                  flex: '1 1 140px',
                  minWidth: '140px',
                }}
              >
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.textHeading }}>{m.value}</div>
                <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '2px' }}>{m.label}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
